import React, { useState, useEffect, ReactElement } from 'react';
import {
  Container,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Paper,
  Tooltip,
} from '@mui/material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  getDFUInformation,
  getDFUFileNames,
  downloadBlob,
  addDFUFile,
  deleteBlob,
  setAsNewest,
} from '../../../repository/DFUDAO';
import { useUserContext } from '../../../context_providers/user_context/UserProvider';
import { useToast } from '../../../context_providers/toast_context/ToastContext';
import { ToastType } from '../../../context_providers/toast_context/ToastContext';
import AddDFUModal from './modals/AddDFUModal';
import InfoModal from '../../../components/modals/utils/InfoModal';
import ConfirmModal from '../../../components/modals/utils/ConfirmModal';
import Loading from '../../../components/loading/Loading';
import LoadingSpinner from '../../../components/loading/LoadingSpinner';
import COLORS from '../../../resources/style/Themes';
import PageHeader from '../../../components/utils/PageHeader';
import { FileDownload, Delete, Info, Add } from '@mui/icons-material';

export const DFUFileManagement = () => {
  const { bearerToken } = useUserContext();
  const [blobData, setBlobData] = useState<{
    [key: string]: string;
  }>();
  const [latestFirmwareVersion, setLatestFirmwareVersion] = useState<
    string | null
  >(null);
  const [showAddDFUModal, setShowAddDFUModal] = useState(false);
  const [infoMessage, setInfoMessage] = useState<ReactElement>();
  const [infoTitle, setInfoTitle] = useState<ReactElement>();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [loadingBlob, setLoadingBlob] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [fileToDelete, setFileToDelete] = useState<string | null>('');
  const { showToast } = useToast();

  const downloadBlobLocal = async (blobName: string) => {
    try {
      await downloadBlob(blobName);

      showToast(`Successfully downloaded ${blobName}.`, ToastType.SUCCESS);
    } catch (error) {
      showToast(
        `Failed to download ${blobName}. Please try again`,
        ToastType.DANGER,
      );
    }
  };

  const deleteBlobLocal = async (blobName: string) => {
    setConfirmTitle('Delete DFU File');
    setConfirmMessage(`Are you sure you want to delete ${blobName}?`);
    setFileToDelete(blobName);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) {
      showToast(
        `File does not exist. Try refreshing the page`,
        ToastType.DANGER,
      );
      console.error('tried to delete file, but it was null');
      return;
    }

    try {
      setLoadingBlob(true);
      const response = await deleteBlob(fileToDelete);

      showToast(`Successfully deleted ${fileToDelete}.`, ToastType.SUCCESS);
      setBlobData(response);
    } catch (error) {
      showToast(
        `Failed to delete ${fileToDelete}. Please try again`,
        ToastType.DANGER,
      );
    } finally {
      setLoadingBlob(false);
      setShowConfirmModal(false);
      setFileToDelete('');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingBlob(true);
      try {
        const fileNamesResponse = await getDFUFileNames();
        if (fileNamesResponse) {
          setBlobData(fileNamesResponse);
        } else {
          showToast(
            'Failed to fetch DFU file names. Please try again.',
            ToastType.DANGER,
          );
        }

        const firmwareResponse = await getDFUInformation();
        if (firmwareResponse) {
          setLatestFirmwareVersion(
            firmwareResponse?.latest_firmware_version ?? '',
          );
        }
      } catch (error) {
        console.log(error);
        showToast(
          'Failed to fetch DFU file names. Please try again.',
          ToastType.DANGER,
        );
      } finally {
        setLoadingBlob(false);
      }
    };
    fetchData();
  }, [bearerToken]);

  const setAsHot = async (fileName: string) => {
    setLoadingBlob(true);
    try {
      const newData = await setAsNewest(fileName);
      setBlobData(newData);
      showToast(`Successfully set ${fileName} as active.`, ToastType.SUCCESS);
    } catch (e) {
      console.error('failed to set as newest', e);
      showToast(
        `Failed to set ${fileName} as active. Please try again.`,
        ToastType.DANGER,
      );
    } finally {
      setLoadingBlob(false);
    }
  };

  const handleUpload = async (file: File) => {
    setLoadingBlob(true);
    try {
      const newData = await addDFUFile(file);
      setBlobData(newData);
      showToast(`Successfully uploaded ${file.name}.`, ToastType.SUCCESS);
      setShowAddDFUModal(false);
    } catch (e) {
      showToast(
        `Failed to upload ${file.name}. Please try again.`,
        ToastType.DANGER,
      );
    } finally {
      setLoadingBlob(false);
    }
  };

  const isLatestFirmwareFile = (fileName: string) => {
    if (!latestFirmwareVersion) return false;
    return fileName === `CompassProDFU_${latestFirmwareVersion}.zip`;
  };

  const renderBlobData = () => (
    <>
      <PageHeader
        disabled={false}
        action={() => setShowAddDFUModal(true)}
        icon={<Add width={14} height={14} />}
        header={'DFU File Management'}
        label={'Add DFU'}
      />
      <TableContainer component={Paper} sx={{ mt: 3, boxShadow: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>File</TableCell>
              <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>
                <span>Status</span>
                <IconButton
                  style={{ color: COLORS.SAFEGUARD_BLUE }}
                  onClick={() => {
                    setInfoTitle(
                      <Typography variant="h6">
                        Compass DFU File Status
                      </Typography>,
                    );
                    setInfoMessage(
                      <Typography>
                        A file being 'Hot' does not affect the functionality of
                        our user-facing application, but rather the pricing of
                        file access on Azure. For this reason, only the most
                        accessed DFU file should be set to 'Hot'.{' '}
                        <a
                          href="https://learn.microsoft.com/en-us/azure/storage/blobs/access-tiers-overview"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: COLORS.SAFEGUARD_BLUE }}
                        >
                          Learn more about Azure Storage access tiers
                        </a>
                      </Typography>,
                    );
                    setShowInfoModal(true);
                  }}
                >
                  <Info />
                </IconButton>
              </TableCell>
              <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>
                Download
              </TableCell>
              <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>
                Remove
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blobData !== undefined &&
              Object.keys(blobData).map((name) => (
                <TableRow key={name}>
                  <TableCell>
                    <Typography fontWeight="bold">{name}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {blobData[name] === 'Hot' ? (
                      'Hot'
                    ) : blobData[name] === 'Cool' ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setAsHot(name)}
                      >
                        Set as Hot
                      </Button>
                    ) : (
                      blobData[name]
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      sx={{
                        borderRadius: '10px',
                        width: '40px',
                        height: '40px',
                        border: 'none',
                        backgroundColor: COLORS.LIGHT_GREEN,
                        '&:hover': {
                          backgroundColor: COLORS.HOVER_GREEN,
                        },
                      }}
                      onClick={() => downloadBlobLocal(name)}
                    >
                      <FileDownload sx={{ color: COLORS.DARK_GREEN }} />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        isLatestFirmwareFile(name)
                          ? 'Cannot delete the latest firmware version file. Change this field in Device Firmware Versioning to allow deletion.'
                          : ''
                      }
                    >
                      <span>
                        <IconButton
                          sx={{
                            backgroundColor: COLORS.PINK,
                            borderRadius: '10px',
                            height: '40px',
                            width: '40px',
                            '&:hover': {
                              backgroundColor: isLatestFirmwareFile(name)
                                ? COLORS.PINK
                                : COLORS.HOVER_RED,
                            },
                            opacity: isLatestFirmwareFile(name) ? 0.5 : 1,
                          }}
                          onClick={() =>
                            !isLatestFirmwareFile(name) && deleteBlobLocal(name)
                          }
                          disabled={isLatestFirmwareFile(name)}
                        >
                          <Delete
                            style={{ color: COLORS.DARK_RED }}
                            width={20}
                            height={20}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return (
    <>
      {loadingBlob ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ height: '50vh' }}
        >
          <CircularProgress color="primary" />
        </Box>
      ) : blobData ? (
        <Container maxWidth="xl">{renderBlobData()}</Container>
      ) : (
        <LoadingSpinner />
      )}
      <AddDFUModal
        show={showAddDFUModal}
        onHide={() => setShowAddDFUModal(false)}
        uploadFile={handleUpload}
      />
      <InfoModal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        infoMessage={infoMessage}
        infoTitle={infoTitle}
      />
      <ConfirmModal
        show={showConfirmModal}
        confirm={handleConfirmDelete}
        onHide={() => {
          setShowConfirmModal(false);
          setFileToDelete(null);
        }}
        confirmMessage={confirmMessage}
        confirmTitle={confirmTitle}
      />
    </>
  );
};

export default withAuthenticationRequired(DFUFileManagement, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
